import request from '../../../util/SuperagentUtils';
import {
  adminInterchangesReportEndpoints,
  buyerInventoryReportEndpoints,
  supplierCurrentInventoryReportEndpoints,
} from '../../../requests/api-endpoints';
import type { AssistantAudience } from '../assistantCapabilities';
import { getSupplierInterchanges } from '../../interchanges/requests/supplier-interchanges-requests';
import { getBuyerInterChanges } from '../../interchanges/requests/buyer-interchanges-requests';

export interface SupplierCheckoutLookupRow {
  activity?: string;
  activityDate?: string;
  locationName?: string;
  orderNumber?: string;
  containerNumber?: string;
  trailerNumber?: string;
  chassisNumber?: string;
}

/** A gate event with the detail the equipment lookup reports on. */
export interface EquipmentActivityRow extends SupplierCheckoutLookupRow {
  truckLicensePlateNumber?: string;
  activityUser?: string;
  driverFirstName?: string;
  driverLastName?: string;
  buyerName?: string;
  supplierName?: string;
  assetType?: string;
  assetSize?: string;
  /** "2 Day(s) 3 Hour(s) 5 Minute(s)" — set on a check-out, from its check-in. */
  dwellTimeDisplay?: string;
  correctionStatus?: string;
}

export interface SupplierCurrentInventoryLookupRow {
  checkInDate?: string;
  locationName?: string;
  orderNumber?: string;
  containerNumber?: string;
  trailerNumber?: string;
  chassisNumber?: string;
}

export const searchSupplierCheckoutHistory = async (
  equipmentNumber: string,
): Promise<SupplierCheckoutLookupRow[]> => {
  const response = await getSupplierInterchanges({
    equipmentNumber,
    activity: 'CHECK_OUT',
    sortBy: 'ACTIVITY_DATE',
    sortDir: 'desc',
    page: '0',
    size: '10',
    isPageable: true,
  });

  return response?.body?.content ?? [];
};

export const searchSupplierCurrentInventory = async (
  equipmentNumber: string,
): Promise<SupplierCurrentInventoryLookupRow[]> => {
  const params = new URLSearchParams({
    equipmentNumber,
    page: '0',
    size: '10',
    sortBy: 'CHECK_IN_DATE',
    sortDir: 'desc',
  });
  const response = await request.get(
    `${supplierCurrentInventoryReportEndpoints.search}?${params.toString()}`,
  );
  return response?.body?.content ?? [];
};

/**
 * Buyer interchanges don't support server-side filtering by activity, so we
 * pull a generous page of this equipment's most recent activity and filter
 * for CHECK_OUT rows client-side. Sized well above what one piece of
 * equipment would realistically rack up between checkouts, so an older
 * CHECK_OUT is not missed behind a run of other activity (corrections,
 * repeated check-ins, etc.) on the same equipment number.
 */
export const searchBuyerCheckoutHistory = async (
  equipmentNumber: string,
): Promise<SupplierCheckoutLookupRow[]> => {
  const response = await getBuyerInterChanges({
    equipmentNumber,
    sortBy: 'ACTIVITY_DATE',
    sortDir: 'desc',
    page: 0,
    size: 100,
  });

  const rows: SupplierCheckoutLookupRow[] = response?.body?.content ?? [];
  return rows.filter((row) => row.activity === 'CHECK_OUT');
};

export const searchBuyerCurrentInventory = async (
  equipmentNumber: string,
): Promise<SupplierCurrentInventoryLookupRow[]> => {
  const params = new URLSearchParams({
    equipmentNumber,
    page: '0',
    size: '10',
    sortBy: 'CHECK_IN_DATE',
    sortDir: 'desc',
  });
  const response = await request.get(
    `${buyerInventoryReportEndpoints.search}?${params.toString()}`,
  );
  return response?.body?.content ?? [];
};

/**
 * Staff search every location's gate activity, so a checkout lookup by an admin
 * reads the platform-wide report rather than one account's.
 */
export const searchAdminCheckoutHistory = async (
  equipmentNumber: string,
): Promise<SupplierCheckoutLookupRow[]> => {
  const response = await request.get(adminInterchangesReportEndpoints.search).query({
    equipmentNumber,
    activity: 'CHECK_OUT',
    sortBy: 'ACTIVITY_DATE',
    sortDir: 'desc',
    page: 0,
    size: 10,
  });
  return response?.body?.content ?? [];
};

/** Gate events fetched per equipment lookup — months of comings and goings. */
export const EQUIPMENT_ACTIVITY_PAGE_SIZE = 25;

/**
 * Gate events recorded for an equipment number, newest first, from whichever
 * interchange search the audience may read.
 *
 * The equipment number is matched **partially** and across container, trailer,
 * chassis and truck-plate numbers, so callers must keep only the rows whose
 * number is exactly the one asked about. No date range is sent: "where is this
 * container" has to find it however long it has been parked.
 */
export const searchEquipmentActivity = async (
  audience: AssistantAudience,
  equipmentNumber: string,
): Promise<EquipmentActivityRow[]> => {
  if (audience === 'admin') {
    const response = await request.get(adminInterchangesReportEndpoints.search).query({
      equipmentNumber,
      sortBy: 'ACTIVITY_DATE',
      sortDir: 'desc',
      page: 0,
      size: EQUIPMENT_ACTIVITY_PAGE_SIZE,
    });
    return response?.body?.content ?? [];
  }

  if (audience === 'supplier') {
    const response = await getSupplierInterchanges({
      equipmentNumber,
      sortBy: 'ACTIVITY_DATE',
      sortDir: 'desc',
      page: '0',
      size: String(EQUIPMENT_ACTIVITY_PAGE_SIZE),
      isPageable: true,
    });
    return response?.body?.content ?? [];
  }

  const response = await getBuyerInterChanges({
    equipmentNumber,
    sortBy: 'ACTIVITY_DATE',
    sortDir: 'desc',
    page: 0,
    size: EQUIPMENT_ACTIVITY_PAGE_SIZE,
  });
  return response?.body?.content ?? [];
};
